import React, { useEffect, useState } from 'react';
import discoveryService from '../../services/discoveryService';
import caseService from '../../services/caseService';

const CaseDiscovery = ({ caseId, caseName }) => {
  const [activeTab, setActiveTab] = useState('documents');
  const [discoverableDocs, setDiscoverableDocs] = useState([]);
  const [opposingCounsel, setOpposingCounsel] = useState([]);
  const [discoveryRequests, setDiscoveryRequests] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Modal states
  const [showAddContact, setShowAddContact] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState('otp');
  const [rowBusy, setRowBusy] = useState(null);

  useEffect(() => {
    loadData();
  }, [caseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [docsData, counselData, requestsData, contactsData] = await Promise.all([
        discoveryService.getDiscoverableDocuments(caseId),
        discoveryService.getCaseContacts(caseId, 'Opposing Counsel'),
        discoveryService.getDiscoveryRequests(caseId),
        caseService.getContacts({ limit: 500 }),
      ]);
      setDiscoverableDocs(docsData.documents || []);
      setOpposingCounsel(counselData.contacts || []);
      setDiscoveryRequests(requestsData.requests || []);
      setAllContacts(contactsData.contacts || []);
    } catch (err) {
      console.error('Failed to load discovery data:', err);
      setLoadError(err.response?.data?.detail || err.message || 'Failed to load discovery data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOpposingCounsel = async () => {
    if (!selectedContact) return;
    try {
      await discoveryService.addCaseContact(caseId, {
        contact_id: selectedContact,
        role: 'Opposing Counsel',
        is_primary: opposingCounsel.length === 0,
      });
      setShowAddContact(false);
      setSelectedContact('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add opposing counsel');
    }
  };

  const handleRemoveContact = async (counsel) => {
    if (!window.confirm('Remove this opposing counsel?')) return;
    try {
      await discoveryService.removeCaseContact(caseId, counsel.contact_id, counsel.role || 'Opposing Counsel');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove contact');
    }
  };

  const handleGenerateDiscovery = async () => {
    if (!selectedContact || selectedDocs.length === 0) {
      alert('Please select a recipient and at least one document');
      return;
    }
    try {
      setGenerating(true);
      const result = await discoveryService.generateDiscoveryRequest(caseId, {
        recipient_contact_id: selectedContact,
        document_ids: selectedDocs,
        expires_days: 30,
        mode: deliveryMode,
      });
      alert(`Discovery package generated for ${result.recipient} — ${result.documents_count} documents.\nThe notification email is queued; watch the Request History status for the send outcome.`);
      setShowGenerateModal(false);
      setSelectedContact('');
      setSelectedDocs([]);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate discovery');
    } finally {
      setGenerating(false);
    }
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAllDocs = () => {
    if (selectedDocs.length === discoverableDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(discoverableDocs.map(d => d.id));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusBadge = (status) => {
    const styles = {
      generated: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
      sent: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
      send_failed: 'bg-red-500/20 text-red-300 border border-red-500/30',
      verified: 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30',
      downloaded: 'bg-green-500/20 text-green-300 border border-green-500/30',
      revoked: 'bg-red-500/20 text-red-300 border border-red-500/30',
      superseded: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
      expired: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
    };
    return styles[status] || 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
  };

  const isDead = (req) => req.status === 'revoked' || req.status === 'superseded';

  const handleRevoke = async (req) => {
    if (!window.confirm('Revoke this discovery link? The recipient will no longer be able to download.')) return;
    try {
      setRowBusy(req.public_id);
      await discoveryService.revokeRequest(req.public_id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRowBusy(null);
    }
  };

  const handleReissue = async (req) => {
    if (!window.confirm(`Re-issue this production as version ${(req.version || 1) + 1}? The current link dies and a fresh link is emailed to ${req.recipient_email}.`)) return;
    try {
      setRowBusy(req.public_id);
      await discoveryService.reissueRequest(req.public_id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to re-issue');
    } finally {
      setRowBusy(null);
    }
  };

  const handleSwitchToPassword = async (req) => {
    if (!window.confirm('Switch this request to link+password mode (no email verification)?')) return;
    try {
      setRowBusy(req.public_id);
      await discoveryService.setRequestMode(req.public_id, 'password');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setRowBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <p className="text-red-300 font-medium mb-1">Discovery data failed to load</p>
          <p className="text-sm text-red-400/80 mb-4">{loadError}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-virgo-navy-600">
        {['documents', 'counsel', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-4 font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-cyan-500 text-cyan-400'
                : 'text-virgo-teal-400 hover:text-virgo-teal-300'
            }`}
          >
            {tab === 'documents' ? 'Discoverable Documents' :
             tab === 'counsel' ? 'Opposing Counsel' : 'Request History'}
          </button>
        ))}
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-virgo-teal-400">
              {discoverableDocs.length} document{discoverableDocs.length !== 1 ? 's' : ''} marked for discovery
            </p>
            {discoverableDocs.length > 0 && opposingCounsel.length > 0 && (
              <button
                onClick={() => setShowGenerateModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium shadow-lg shadow-cyan-500/30 transition-all"
              >
                Generate Discovery Package
              </button>
            )}
          </div>

          {discoverableDocs.length === 0 ? (
            <div className="text-center py-12 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl">
              <p className="text-virgo-teal-400 mb-2">No documents marked as discoverable</p>
              <p className="text-sm text-virgo-teal-500">
                Go to the Documents tab and check the "Discoverable" box on files to include them
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {discoverableDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl hover:border-cyan-500/30 transition-all">
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl">
                      {doc.mime_type?.includes('pdf') ? '📕' : '📄'}
                    </span>
                    <div>
                      <p className="font-medium text-white">{doc.file_name}</p>
                      <p className="text-sm text-virgo-teal-400">
                        {doc.category} • {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                  {doc.discovery_notes && (
                    <span className="text-sm text-virgo-teal-500 italic">{doc.discovery_notes}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Opposing Counsel Tab */}
      {activeTab === 'counsel' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-virgo-teal-400">Manage opposing counsel for discovery delivery</p>
            <button
              onClick={() => setShowAddContact(true)}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium shadow-lg shadow-cyan-500/30 transition-all"
            >
              + Add Opposing Counsel
            </button>
          </div>

          {opposingCounsel.length === 0 ? (
            <div className="text-center py-12 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl">
              <p className="text-virgo-teal-400">No opposing counsel added to this case</p>
            </div>
          ) : (
            <div className="space-y-2">
              {opposingCounsel.map((counsel) => (
                <div key={`${counsel.contact_id}-${counsel.role}`} className="flex items-center justify-between p-4 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl hover:border-cyan-500/30 transition-all">
                  <div>
                    <p className="font-medium text-white">{counsel.contact_name}</p>
                    <p className="text-sm text-virgo-teal-400">{counsel.contact_email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {counsel.is_primary && (
                      <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded">Primary</span>
                    )}
                    <button
                      onClick={() => handleRemoveContact(counsel)}
                      className="text-red-400 hover:text-red-300 p-2 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {discoveryRequests.length === 0 ? (
            <div className="text-center py-12 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl">
              <p className="text-virgo-teal-400">No discovery requests sent yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl">
              <table className="min-w-full divide-y divide-virgo-navy-600">
                <thead className="bg-virgo-navy-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Recipient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Files</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">V</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Downloaded</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-virgo-navy-600">
                  {discoveryRequests.map((req) => (
                    <tr key={req.public_id} className={`hover:bg-virgo-navy-600/50 transition-colors ${isDead(req) ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-white">{req.recipient_name}</p>
                        <p className="text-sm text-virgo-teal-400">{req.recipient_email}</p>
                      </td>
                      <td className="px-4 py-3 text-virgo-teal-200">
                        {req.total_files} files ({formatFileSize(req.total_size_bytes)})
                      </td>
                      <td className="px-4 py-3 text-sm text-virgo-teal-200">v{req.version || 1}</td>
                      <td className="px-4 py-3 text-sm text-virgo-teal-200">
                        {req.mode === 'otp' ? 'OTP' : 'password'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded ${getStatusBadge(req.status)}`}>
                          {req.status === 'send_failed' ? 'send failed' : req.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        {req.email_sent_at ? new Date(req.email_sent_at).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        {req.downloaded_at ? new Date(req.downloaded_at).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {!isDead(req) && (
                          <>
                            <button
                              onClick={() => handleReissue(req)}
                              disabled={rowBusy === req.public_id}
                              className="text-cyan-400 hover:text-cyan-300 mr-3 disabled:opacity-50"
                              title="Supersede with a fresh link (new version)"
                            >
                              Re-issue
                            </button>
                            {req.mode === 'otp' && (
                              <button
                                onClick={() => handleSwitchToPassword(req)}
                                disabled={rowBusy === req.public_id}
                                className="text-amber-400 hover:text-amber-300 mr-3 disabled:opacity-50"
                                title="Drop to link+password mode"
                              >
                                To&nbsp;pwd
                              </button>
                            )}
                            <button
                              onClick={() => handleRevoke(req)}
                              disabled={rowBusy === req.public_id}
                              className="text-red-400 hover:text-red-300 disabled:opacity-50"
                              title="Kill this link immediately"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Add Opposing Counsel</h3>
            <select
              value={selectedContact}
              onChange={(e) => setSelectedContact(e.target.value)}
              className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 mb-4"
            >
              <option value="">Select a contact...</option>
              {allContacts
                .filter(c => c.primary_email_address)
                .map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.contact_name} ({contact.primary_email_address})
                  </option>
                ))}
            </select>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowAddContact(false); setSelectedContact(''); }}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddOpposingCounsel}
                disabled={!selectedContact}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Discovery Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Generate Discovery Package</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                Send to Opposing Counsel
              </label>
              <select
                value={selectedContact}
                onChange={(e) => setSelectedContact(e.target.value)}
                className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Select recipient...</option>
                {opposingCounsel.map((counsel) => (
                  <option key={counsel.contact_id} value={counsel.contact_id}>
                    {counsel.contact_name} ({counsel.contact_email})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-virgo-teal-400">
                  Select Documents ({selectedDocs.length} selected)
                </label>
                <button
                  onClick={selectAllDocs}
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  {selectedDocs.length === discoverableDocs.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border border-virgo-teal-700/30 rounded-lg max-h-64 overflow-y-auto bg-virgo-navy-800/50">
                {discoverableDocs.map((doc) => (
                  <label
                    key={doc.id}
                    className="flex items-center p-3 hover:bg-virgo-navy-600/50 cursor-pointer border-b border-virgo-navy-600 last:border-b-0 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDocs.includes(doc.id)}
                      onChange={() => toggleDocSelection(doc.id)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                    />
                    <span className="ml-3 text-white">{doc.file_name}</span>
                    <span className="ml-auto text-sm text-virgo-teal-400">{formatFileSize(doc.file_size)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Recipient verification</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="deliveryMode" checked={deliveryMode === 'otp'}
                         onChange={() => setDeliveryMode('otp')} className="mt-1" />
                  <span className="text-sm text-virgo-teal-200">
                    <strong className="text-white">Email verification (recommended)</strong> — recipient enters a
                    one-time code sent to their address; the ZIP password is shown after verification.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="deliveryMode" checked={deliveryMode === 'password'}
                         onChange={() => setDeliveryMode('password')} className="mt-1" />
                  <span className="text-sm text-virgo-teal-200">
                    <strong className="text-white">Link + password</strong> — no verification; the ZIP password
                    arrives in a second email after download.
                  </span>
                </label>
              </div>
            </div>

            <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-200">
                <strong className="text-amber-300">Every production is manifested:</strong> each document's SHA-256
                is fixed at generation, re-verified at download, and a MANIFEST.pdf ships inside the archive.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowGenerateModal(false); setSelectedContact(''); setSelectedDocs([]); }}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateDiscovery}
                disabled={generating || !selectedContact || selectedDocs.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
              >
                {generating ? 'Generating…' : 'Send Discovery'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDiscovery;
